import {
  Button,
  ButtonColorEnum,
  ButtonSizeEnum,
  Checkbox,
  CloseIcon,
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  FormInput,
  FormLabel,
  IconButton,
  IconButtonColorEnum,
  IconButtonSizeEnum,
  IconSizeEnum,
  LoaderIcon,
  PlusIcon,
  Select,
  SelectOptionProps,
  Tooltip,
  Typography,
  WarningInfoIcon,
} from '@acceso/design-system';
import clsx from 'clsx';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuiv4 } from 'uuid';

import { useCatalogueSelectProps } from '@/components/CatalogueListbox/use-catalogue-select-props';
import { PortalTooltip } from '@/components/PortalTooltip/PortalTooltip';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { findSegmentByDate } from '@/features/broadcast-schedule-program/helpers/find-segment-by-date';
import { isEnrichmentOutsideCut } from '@/features/broadcast-schedule-program/helpers/is-enrichment-outside-cut';
import { usePeoplePicker } from '@/features/featured-people/hooks/use-people-picker';
import { useFeaturedPeoplePermissions } from '@/features/featured-people/hooks/useFeaturedPeoplePermissions';
import { useSaveDeclarantAsPeople } from '@/features/featured-people/hooks/useSaveDeclarantAsPeople';
import { useGetSegmentId } from '@/features/segments/api/get-segment-id';
import { useNotificationStore } from '@/stores/notifications';
import { convertDatesToSeconds, getAbsoluteDateFromPlayerTime } from '@/utils/hh-mm-ss-into-number';

import { TSegmentDeclarationClip } from '../BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitCuts/types';

type TAddStatements = {
  statements: TSegmentDeclarationClip[];
  setStatements: Dispatch<SetStateAction<TSegmentDeclarationClip[]>>;
  showStatementSelect: boolean;
  setShowStatementSelect: Dispatch<SetStateAction<boolean>>;
  showWarningStatements?: boolean;
  cutStart?: Date | null;
  cutEnd?: Date | null;
  disabled?: boolean;
};

export function AddStatements({
  statements,
  setStatements,
  showStatementSelect,
  setShowStatementSelect,
  showWarningStatements = false,
  cutStart = null,
  cutEnd = null,
  disabled = false,
  isLoadingPreview,
}: TAddStatements & { isLoadingPreview?: boolean }) {
  const {
    selectedMention,
    activeSegment,
    segmentItems,
    selectedMentionTime,
    playerVideoCurrentTime,
    setSelectedMention,
    setSelectedMentionTime,
    setTextToHighlight,
    setActiveSegment,
    setPendingSeekTime,
    setVisibleBox,
    videoControlRef,
  } = useBroadcastScheduleProgram();
  const { t } = useTranslation();
  const peoplePicker = usePeoplePicker({ value: null });
  const peopleSelectProps = useCatalogueSelectProps(peoplePicker);
  const [segmentIdToFetch, setSegmentIdToFetch] = useState<string | null>(null);
  const { data: fetchedSegment } = useGetSegmentId({ id: segmentIdToFetch || '' });
  const { addWarningNotification } = useNotificationStore();
  const [newPerson, setNewPerson] = useState(false);
  const [newPersonName, setNewPersonName] = useState('');
  const [savePersonAsPeople, setSavePersonAsPeople] = useState(false);
  const { canCreate: canCreatePeople } = useFeaturedPeoplePermissions();
  const { linkDeclarant, isSaving: isSavingPeople } = useSaveDeclarantAsPeople();

  useEffect(() => {
    if (fetchedSegment) {
      setActiveSegment(fetchedSegment);
      setSegmentIdToFetch(null);
    }
  }, [fetchedSegment]);

  const peopleRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [tooltip, setTooltip] = useState<null | {
    x: number;
    y: number;
    statement: TSegmentDeclarationClip;
  }>(null);
  const [highlightedStatementId, setHighlightedStatementId] = useState<string | null>(null);

  // The picker keeps one option object per person, so each serves as its own value.
  const mentionOptions: SelectOptionProps[] = useMemo(
    () =>
      peoplePicker.options.map(option => ({
        image: option.image,
        label: option.label,
        value: option,
      })),
    [peoplePicker.options]
  );

  const handleClickAddStatements = useCallback(() => {
    if (disabled) return;

    setShowStatementSelect(prev => {
      if (prev) {
        setSelectedMention(null);
        setSelectedMentionTime(null);
        setTextToHighlight(null);
      }
      return !prev;
    });
  }, [disabled]);

  const handleDeleteStatement = useCallback(
    (tempId: string) => {
      setStatements(prev => prev.filter(person => person.tempId !== tempId));
      setTextToHighlight(null);

      setTooltip(prevTooltip => {
        if (prevTooltip?.statement.tempId === tempId) {
          return null;
        }
        return prevTooltip;
      });
    },
    [setStatements]
  );

  const handleChangeStatementSelect = useCallback(
    (value: SelectOptionProps) => {
      if (!activeSegment) throw new Error('activeSegment is undefined');

      const statementDateTime =
        selectedMentionTime ||
        getAbsoluteDateFromPlayerTime(activeSegment.start_datetime, playerVideoCurrentTime);

      const newStatement: any = {
        tempId: uuiv4(),
        text: selectedMention || '',
        people_id: value.value,
        image: value.image,
        entity_label: value.label,
        s_datetime: statementDateTime?.toISOString(),
        e_datetime: statementDateTime?.toISOString(),
        addedManually: true,
        parameters: {
          segment_id: activeSegment.id,
        },
      };
      setStatements(prev => [...prev, newStatement]);
      setShowStatementSelect(false);
      setSelectedMention(null);
      setSelectedMentionTime(null);
    },
    [selectedMention, selectedMentionTime, activeSegment, playerVideoCurrentTime]
  );

  const handleAddNewPerson = useCallback(async () => {
    if (!activeSegment) throw new Error('activeSegment is undefined');
    if (!newPersonName.trim()) return;
    // Enter reaches this without going through the button, which is the only part
    // of the UI disabled while saving: without the guard, a double press creates
    // the person twice.
    if (isSavingPeople) return;

    // Bail out when the declarant could not be saved, keeping what was typed so the
    // user can retry or fall back to the one-off declarant.
    const declarant = await linkDeclarant(
      { people_id: null as string | null, entity_label: newPersonName.trim() },
      savePersonAsPeople
    );
    if (!declarant) return;

    const statementDateTime =
      selectedMentionTime ||
      getAbsoluteDateFromPlayerTime(activeSegment.start_datetime, playerVideoCurrentTime);

    const newStatement: any = {
      tempId: uuiv4(),
      text: selectedMention || '',
      people_id: declarant.people_id,
      image: '',
      entity_label: declarant.entity_label,
      s_datetime: statementDateTime?.toISOString(),
      e_datetime: statementDateTime?.toISOString(),
      addedManually: true,
      parameters: {
        segment_id: activeSegment.id,
      },
    };
    setStatements(prev => [...prev, newStatement]);
    setShowStatementSelect(false);
    setSelectedMention(null);
    setSelectedMentionTime(null);
    setNewPersonName('');
    setNewPerson(false);
    setSavePersonAsPeople(false);
  }, [
    selectedMention,
    selectedMentionTime,
    activeSegment,
    playerVideoCurrentTime,
    newPersonName,
    savePersonAsPeople,
    linkDeclarant,
    isSavingPeople,
  ]);

  const handlePreview = useCallback(
    (box: TSegmentDeclarationClip) => {
      if (!segmentItems) throw new Error('segmentItems is undefined');

      const statementId = box.tempId || box.id || null;

      if (highlightedStatementId === statementId) {
        setTextToHighlight(null);
        setHighlightedStatementId(null);
        return;
      }

      setTextToHighlight(box.text || '');
      setHighlightedStatementId(statementId);
      setVisibleBox(null);

      const startDate = box.s_datetime || box.start_datetime;
      const segmentToUpdate = findSegmentByDate(segmentItems, startDate);

      let seconds = 0;
      if (segmentToUpdate) {
        seconds = convertDatesToSeconds(startDate, segmentToUpdate.start_datetime);
        if (activeSegment?.id !== segmentToUpdate.id) {
          setActiveSegment(segmentToUpdate);
          setPendingSeekTime(seconds);
        } else {
          videoControlRef.current?.seekToSecond(seconds || 0);
        }
      } else {
        addWarningNotification(
          'No se encontró el segmento para la fecha seleccionada.',
          'Error al buscar segmento'
        );
        return;
      }
    },
    [segmentItems, highlightedStatementId]
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key.toLocaleLowerCase() === 'd') {
        event.preventDefault();

        if (!selectedMention) return;
        handleClickAddStatements();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleClickAddStatements, selectedMention]);

  const handleMouseEnter = (statement: TSegmentDeclarationClip) => {
    const el = peopleRefs.current[statement.tempId || statement.id || ''];
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;

    const x = rect.left + scrollX + rect.width / 2;
    const y = rect.top + scrollY;

    setTooltip({ x, y, statement });
  };

  const handleMouseLeave = () => {
    setTooltip(null);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex justify-between gap-2">
        <div className="flex items-center gap-1">
          <Typography
            fontSize={FontSizeEnum.Lg}
            weight={FontWeightEnum.Bold}
            color={FontColorEnum.Medium20}
          >
            {t('Declaraciones incluidas')}:
          </Typography>
          <Typography
            fontSize={FontSizeEnum.Lg}
            color={FontColorEnum.Medium20}
            weight={FontWeightEnum.Semibold}
          >
            {statements.length}
          </Typography>
          {showWarningStatements && (
            <Tooltip help={t('Una o varias declaraciones no entran dentro del tiempo del corte')}>
              <WarningInfoIcon className="text-red-500" />
            </Tooltip>
          )}
        </div>

        <Tooltip
          help={
            <>
              <Typography fontSize={FontSizeEnum.Xs} color={FontColorEnum.Neutral}>
                {t('Pasos')}:
                <br />
                <br />
              </Typography>
              <ol>
                <li>
                  <Typography fontSize={FontSizeEnum.Xs} color={FontColorEnum.Neutral}>
                    {t('Pulsar sobre el botón más')}
                  </Typography>
                </li>
                <li>
                  <Typography fontSize={FontSizeEnum.Xs} color={FontColorEnum.Neutral}>
                    {t('Seleccionas el texto de la declaración en el STT')}
                  </Typography>
                </li>
                <li>
                  <Typography fontSize={FontSizeEnum.Xs} color={FontColorEnum.Neutral}>
                    {t('Selecciona o crea uno nuevo en el desplegable')}
                  </Typography>
                </li>
              </ol>
            </>
          }
        >
          <IconButton
            size={IconButtonSizeEnum.SM}
            color={IconButtonColorEnum.DEFAULT_REVERSE}
            disabled={disabled}
          >
            <PlusIcon
              onClick={handleClickAddStatements}
              size={IconSizeEnum.SM}
              disabled={disabled}
            />
          </IconButton>
        </Tooltip>
      </div>

      {isLoadingPreview ? (
        <div className="flex w-full justify-center">
          <LoaderIcon className="animate-spin" />
        </div>
      ) : (
        <div className="flex justify-between">
          <div className="flex flex-wrap gap-2">
            {statements.map(person => {
              const isOutsideCut = isEnrichmentOutsideCut(person, cutStart, cutEnd);

              return (
                <div
                  className={clsx(
                    'relative flex w-fit cursor-pointer flex-nowrap items-center gap-1 rounded-md border-[1px] px-2 text-basic',
                    isOutsideCut
                      ? 'border-feedback-error-20 text-feedback-error-20'
                      : 'border-text-dark-10 text-text-dark-10',
                    highlightedStatementId === (person.tempId || person.id) && '!bg-bg-primary-25',
                    'hover:bg-bg-primary-20'
                  )}
                  key={person.tempId}
                  ref={el => (peopleRefs.current[person.tempId || person.id || ''] = el)}
                  onClick={() => handlePreview(person)}
                  onMouseEnter={() => handleMouseEnter(person)}
                  onMouseLeave={handleMouseLeave}
                >
                  <Typography
                    className="max-w-[120px] overflow-hidden text-ellipsis whitespace-nowrap"
                    fontSize={FontSizeEnum.Basic}
                    color={isOutsideCut ? FontColorEnum.Error20 : FontColorEnum.Dark10}
                  >
                    {person.entity_label}
                  </Typography>
                  {
                    <CloseIcon
                      size={IconSizeEnum.SM}
                      onClick={event => {
                        event.stopPropagation();
                        handleDeleteStatement(person?.tempId || '');
                      }}
                    />
                  }
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showStatementSelect && (
        <div className="flex flex-col gap-2">
          <div className="flex w-full items-center justify-between">
            <FormLabel>{t('Persona')}</FormLabel>
            <Button
              size={ButtonSizeEnum.XS}
              color={ButtonColorEnum.DEFAULT_GHOST}
              onClick={() => {
                setNewPerson(prev => !prev);
                setNewPersonName('');
                setSavePersonAsPeople(false);
              }}
            >
              <Typography
                fontSize={FontSizeEnum.Sm}
                color={FontColorEnum.Dark10}
                weight={FontWeightEnum.Medium}
              >
                {newPerson ? t('Existente') : t('Nueva')}
              </Typography>
            </Button>
          </div>
          {newPerson ? (
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <FormInput
                    value={newPersonName}
                    onChange={e => setNewPersonName(e.target.value)}
                    placeholder={t('Persona')}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddNewPerson();
                      }
                    }}
                  />
                </div>
                <Button
                  size={ButtonSizeEnum.SM}
                  color={ButtonColorEnum.DEFAULT}
                  onClick={handleAddNewPerson}
                  disabled={!newPersonName.trim() || disabled || isSavingPeople}
                  className="flex-shrink-0"
                >
                  <Typography
                    fontSize={FontSizeEnum.Sm}
                    color={FontColorEnum.Neutral}
                    weight={FontWeightEnum.Medium}
                  >
                    {t('Añadir')}
                  </Typography>
                </Button>
              </div>
              {canCreatePeople && (
                <Checkbox
                  labelClassName="ml-1"
                  checked={savePersonAsPeople}
                  disabled={disabled || isSavingPeople}
                  onChange={() => setSavePersonAsPeople(prev => !prev)}
                >
                  <Typography fontSize={FontSizeEnum.Sm} color={FontColorEnum.Dark10}>
                    {t('Guardar en Personas de Interés')}
                  </Typography>
                </Checkbox>
              )}
            </div>
          ) : (
            <Select
              {...peopleSelectProps}
              onChange={handleChangeStatementSelect}
              options={mentionOptions}
              disabled={disabled}
            />
          )}
        </div>
      )}

      {tooltip && (
        <PortalTooltip x={tooltip.x} y={tooltip.y}>
          <div className="flex flex-col gap-2">
            <div className="flex">
              <Typography fontSize={FontSizeEnum.Sm} color={FontColorEnum.Neutral}>
                {tooltip.statement.entity_label}
              </Typography>
            </div>
            {isEnrichmentOutsideCut(tooltip.statement, cutStart, cutEnd) && (
              <div className="flex">
                <Typography fontSize={FontSizeEnum.Xs} color={FontColorEnum.Neutral}>
                  {t('El tiempo de la declaración no entra dentro del tiempo del corte')}
                </Typography>
              </div>
            )}
          </div>
        </PortalTooltip>
      )}
    </div>
  );
}
